'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { getPropertyMetadata } from '@gnsx/genesys.js';
import { get } from 'es-toolkit/compat';

import type { EditorPropertyMeta } from '@/editor/property-editor/types';
import type { PropertyMetadata } from '@gnsx/genesys.js';

import { isSubclass } from '@/editor/core/classHelpers';

export function getPropertyMeta(classMeta: ENGINE.EditorClassMeta | EditorPropertyMeta | PropertyMetadata | undefined, path: string): PropertyMetadata | EditorPropertyMeta | undefined {
  path = path.startsWith('options.') ? path.replace('options.', '') : path;

  // PropertyMetadata doesn't have nested properties, so return undefined
  if (classMeta && 'type' in classMeta) {
    return undefined;
  }

  // Legacy EditorClassMeta/EditorPropertyMeta lookup
  return get(classMeta, path);
}

export function getUnifiedPropertyMetadata(target: any): Record<string, PropertyMetadata> {
  if (!target) return {};

  const constructor = target.constructor;
  if (!constructor) return {};

  // Try to get the registered class name from ClassRegistry's reverse registry
  // This is more reliable than using constructor.name which gets minified in production
  const className = (ENGINE as any).ClassRegistry?.reverseRegistry?.get?.(constructor);

  // If we found a registered class name, get the fresh constructor from the registry
  // This helps with cache invalidation after builds (ENGINE classes only)
  let finalConstructor = constructor;
  if (className) {
    const freshConstructor = (ENGINE as any).ClassRegistry?.getClass?.(className);
    if (freshConstructor) {
      finalConstructor = freshConstructor;
    }
  }

  // Returns from both @property, and PROPERTY_METADATA
  return getPropertyMetadata(finalConstructor.prototype);
}

/**
 * getUnifiedPropertyMeta - Gets metadata for a single property from the unified metadata system
 *
 * This is the unified version of getPropertyMeta that works with PropertyMetadata
 *
 * @param target - The component/actor instance
 * @param propertyName - The name of the property (with or without 'options.' prefix)
 * @returns PropertyMetadata for the property, or undefined if not found
 */
export function getUnifiedPropertyMeta(target: any, propertyName: string): PropertyMetadata | undefined {
  const metadata = getUnifiedPropertyMetadata(target);

  // Remove 'options.' prefix if present
  const cleanName = propertyName.startsWith('options.') ? propertyName.replace('options.', '') : propertyName;

  return metadata[cleanName];
}

export function getPropertySelectOptions(propertyMeta: any): ENGINE.EditorClassMeta[string]['select'] | undefined {
  return propertyMeta?.select;
}

export const getPropertyClassOrAssetType = (propertyMeta: any): ENGINE.EditorClassMeta[string]['type'] | undefined => {
  return propertyMeta?.type;
};

export const getPropertyArrayMeta = (propertyMeta: any): { minLength?: number; maxLength?: number; child?: EditorPropertyMeta } | undefined => {
  return propertyMeta?.array;
};

export const getPropertyNumberMeta = (propertyMeta: any): { min?: number; max?: number; step?: number; decimals?: number; unit?: string; } | undefined => {
  return propertyMeta?.number;
};

export const getPropertyIntegerMeta = (propertyMeta: any): { min?: number; max?: number; step?: number; unit?: string; } | undefined => {
  return propertyMeta?.integer;
};

export const getPropertyIsHidden = (propertyMeta: any): boolean => {
  return propertyMeta?.hidden ?? false;
};

export const getPropertyIsReadonly = (propertyMeta: any): boolean => {
  return propertyMeta?.readonly ?? false;
};

/**
 * Helper functions for working with PropertyMetadata (unified system)
 */

export const getPropertyMetadataSelectOptions = (propertyMeta: PropertyMetadata | undefined): string[] | Record<string, string> | undefined => {
  if (!propertyMeta) return undefined;

  // PropertyMetadata format (enum type with options)
  if (propertyMeta.type === 'enum' && 'options' in propertyMeta) {
    return propertyMeta.options;
  }

  return undefined;
};

export const getPropertyMetadataArrayMeta = (propertyMeta: PropertyMetadata | undefined): { minLength?: number; maxLength?: number; child?: PropertyMetadata } | undefined => {
  if (!propertyMeta) return undefined;

  // PropertyMetadata format (array type with child)
  if (propertyMeta.type === 'array') {
    return {
      child: 'child' in propertyMeta ? propertyMeta.child : undefined
    };
  }

  return undefined;
};

export const getPropertyMetadataNumberMeta = (propertyMeta: PropertyMetadata | undefined): { min?: number; max?: number; step?: number; decimals?: number; unit?: string; } | undefined => {
  if (!propertyMeta) return undefined;

  // PropertyMetadata format (number type with min/max)
  if (propertyMeta.type === 'number') {
    return {
      min: 'min' in propertyMeta ? propertyMeta.min : undefined,
      max: 'max' in propertyMeta ? propertyMeta.max : undefined,
      step: 'step' in propertyMeta ? propertyMeta.step : undefined,
      decimals: 'decimals' in propertyMeta ? propertyMeta.decimals : undefined,
      unit: 'unit' in propertyMeta ? propertyMeta.unit : undefined
    };
  }

  return undefined;
};

export const getPropertyMetadataIntegerMeta = (propertyMeta: PropertyMetadata | undefined): { min?: number; max?: number; step?: number; unit?: string; } | undefined => {
  if (!propertyMeta) return undefined;

  // PropertyMetadata format doesn't have separate integer type, use number metadata
  if (propertyMeta.type === 'number') {
    return {
      min: 'min' in propertyMeta ? propertyMeta.min : undefined,
      max: 'max' in propertyMeta ? propertyMeta.max : undefined,
      step: 'step' in propertyMeta ? propertyMeta.step : undefined,
      unit: 'unit' in propertyMeta ? propertyMeta.unit : undefined
    };
  }

  return undefined;
};

export const getPropertyMetadataMultiSelectMeta = (propertyMeta: PropertyMetadata | undefined): {
  options?: string[] | Record<string, string>;
  placeholder?: string;
  maxSelected?: number;
  creatable?: boolean;
  useDisplayCase?: boolean;
} | undefined => {
  if (!propertyMeta) return undefined;

  // PropertyMetadata format (multiselect type with options and config)
  if ((propertyMeta.type as string) === 'multiselect') {
    const meta = propertyMeta as any;
    return {
      options: 'options' in propertyMeta ? meta.options : undefined,
      placeholder: 'placeholder' in propertyMeta ? meta.placeholder : undefined,
      maxSelected: 'maxSelected' in propertyMeta ? meta.maxSelected : undefined,
      // Support both 'creatable' and 'createNewAllowed' (engine uses createNewAllowed)
      creatable: ('creatable' in propertyMeta ? meta.creatable : undefined) ?? ('createNewAllowed' in propertyMeta ? meta.createNewAllowed : undefined),
      useDisplayCase: 'useDisplayCase' in propertyMeta ? meta.useDisplayCase : undefined,
    };
  }

  return undefined;
};

export const getPropertyMetadataClassOrAssetType = (propertyMeta: PropertyMetadata | undefined): any => {
  if (!propertyMeta) return undefined;

  // PropertyMetadata format - check for asset types
  const assetTypes = ENGINE.RESOURCE_PATH_TYPES;
  if (assetTypes.includes(propertyMeta.type as typeof assetTypes[number])) {
    return propertyMeta.type;
  }

  return undefined;
};

export function isPropertyClassTypeOf(propertyMeta: any, classToCheck: Function): boolean {
  const classType = getPropertyClassOrAssetType(propertyMeta);
  return classType ? isSubclass(classType as Function, classToCheck) : false;
}

export const DEFAULT_DISPLAY_ORDER: Record<string, number> = {
  prefab: -30,
  uuid: -20,
  name: -19,
  actorTags: -18,
  transform: -10,
  common: -5,
  default: 0,
  physics: 90,
  userData: 100,
  advanced: 1000,
} as const;

// export const getPropertyDisplayOrder = (target: any, key: string) => {
//   const meta = getPropertyMeta(target, key);
//   return meta?.displayOrder ?? 0;
// };
